import { IconMoon } from '@/app/common/icons/libraryIcons';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { useSearchParams } from 'next/navigation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters';
import { useTranslation } from '../../translations/Translation';
import { formatFlightArrivalDay, getTripNights } from '../common/dates';
import { useOffersContext } from '../OffersProvider';
import customCss from '../css/customCss';

const StyledIconMoon = styled(IconMoon)`
  min-width: 16px;
  min-height: 16px;
  color: ${(p) => p.theme.kallisto.color.icon.static.iconSecondary};
`;

const MoreTripThanHotelNightsContainer = styled.div<{ $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  ${(p) =>
    p.$highlighted
      ? css`
          box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
        `
      : css`
          @media ${customCss.breakpoints.lg} {
            box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
          }
        `}
`;

export type MoreTripThanHotelNightsMessageProps = {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  onMessageClicked: () => void;
  highlighted?: boolean;
} & React.HtmlHTMLAttributes<HTMLElement>;

export const MoreTripThanHotelNightsMessage: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<MoreTripThanHotelNightsMessageProps>>
> = (props: MoreTripThanHotelNightsMessageProps) => {
  const { localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { t } = useTranslation();
  const searchParams = useSearchParams();

  if (!offersData?.nights) return null;

  const { outbound, inbound, highlighted, onMessageClicked } = props;

  // Check if custom accommodation is active
  const customAccommodationParam = searchParams.get(URLParameters.CUSTOM_CHECKIN_CHECKOUT);
  const hasCustomAccommodation = !!customAccommodationParam;

  if (hasCustomAccommodation) {
    return null;
  }

  const tripNights = getTripNights(outbound.departureDate, inbound.departureDate);
  const moreTripThanHotelNights = tripNights > offersData.nights;

  return moreTripThanHotelNights ? (
    <MoreTripThanHotelNightsContainer $highlighted={highlighted} onClick={onMessageClicked}>
      <StyledIconMoon />
      <Text variant="BodySmall">
        {offersData.nights === 1
          ? t('vacation.select.flight.message.different_night', {
              ARRIVAL_DATE: formatFlightArrivalDay(localization.locale, outbound.arrivalDate),
              SEARCH_NIGHTS: `${tripNights}`,
            })
          : t('vacation.select.flight.message.different_nights', {
              ARRIVAL_DATE: formatFlightArrivalDay(localization.locale, outbound.arrivalDate),
              FLIGHT_NIGHTS: offersData.nights.toString(),
              SEARCH_NIGHTS: `${tripNights}`,
            })}
      </Text>
    </MoreTripThanHotelNightsContainer>
  ) : null;
};
